import React, { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { ArrowLeft, ExternalLink, AlertCircle, Shield, MessageSquare } from 'lucide-react'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'

const getPreviewUrl = (site) => {
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const SitePreview = () => {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const backPath = searchParams.get('back') || '/client/sites'
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)

  const { data: site, isLoading, isError } = useQuery(
    ['site-preview', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { staleTime: 60000, retry: 1 }
  )

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-0)' }}>
        <LoadingSpinner size="lg" text="Loading preview..." />
      </div>
    )
  }

  if (isError || !site) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, background: 'var(--bg-0)' }}>
        <AlertCircle style={{ width: 40, height: 40, color: 'var(--danger)' }} />
        <p style={{ color: 'var(--text-soft)' }}>Failed to load site preview.</p>
        <button className="btn" onClick={() => navigate(backPath)}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Go Back
        </button>
      </div>
    )
  }

  const previewUrl = getPreviewUrl(site)
  const displayDomain = site.custom_domain || site.azure_published_url?.replace('https://', '') || site.slug || 'preview'

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-0)', zIndex: 50 }}>
      {/* Browser chrome */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', flexShrink: 0 }}>
        <button className="site-action" title="Back" onClick={() => navigate(backPath)}>
          <ArrowLeft style={{ width: 14, height: 14 }} />
        </button>
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--danger)' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--warning)' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--success)' }} />
        </div>
        {/* URL bar */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 999, maxWidth: 520, margin: '0 auto', fontSize: 12.5, color: 'var(--text-soft)', fontFamily: 'var(--font-mono)' }}>
          <Shield style={{ width: 12, height: 12, color: 'var(--success)', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayDomain}</span>
        </div>
        <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
          <ExternalLink style={{ width: 13, height: 13 }} /> Visit live site
        </a>
        <button className="btn btn-sm btn-primary" onClick={() => navigate('/client/requests/new')}>
          <MessageSquare style={{ width: 13, height: 13 }} /> Request changes
        </button>
      </div>

      {/* iframe */}
      <div style={{ flex: 1, position: 'relative', background: 'var(--bg-0)' }}>
        {!iframeLoaded && !iframeError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" text="Loading site..." />
          </div>
        )}
        {iframeError ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <AlertCircle style={{ width: 40, height: 40, color: 'var(--text-faint)' }} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 500, color: 'var(--text-soft)', fontSize: 14, marginBottom: 4 }}>Preview not available</p>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', maxWidth: 320 }}>This site may not have been published yet or is loading.</p>
            </div>
            <button className="btn btn-sm" onClick={() => window.open(previewUrl, '_blank')}>
              <ExternalLink style={{ width: 13, height: 13 }} /> Try opening directly
            </button>
          </div>
        ) : (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={`Preview: ${site.name}`}
            onLoad={() => setIframeLoaded(true)}
            onError={() => setIframeError(true)}
          />
        )}
      </div>
    </div>
  )
}

export default SitePreview
